import React, { useState, useEffect, useRef } from 'react';
import ApiService from '../services/api';
import { TldInfo } from '../types';
import config from '../config';

interface BulkCheckResult {
  tld: string;
  register_date: string;
  expiry_date: string;
  protocol: string;
  whois_server: string;
  rdap_server: string;
  success: boolean;
  errors?: string[];
  timestamp: string;
  test_domain: string;
}

interface BulkCheckStatus {
  running: boolean;
  progress: number;
  total: number;
  current_tld: string;
  current_domain: string;
  results: BulkCheckResult[];
  start_time: string | null;
  end_time: string | null;
  successful: number;
  failed: number;
  log_file: string | null;
  error: string | null;
}

interface LogFile {
  filename: string;
  size: number;
  created: string;
  modified: string;
}

const BulkTldCheck: React.FC = () => {
  const [tlds, setTlds] = useState<TldInfo[]>([]);
  const [inactiveTlds, setInactiveTlds] = useState<TldInfo[]>([]);
  const [status, setStatus] = useState<BulkCheckStatus>({
    running: false,
    progress: 0,
    total: 0,
    current_tld: '',
    current_domain: '',
    results: [],
    start_time: null,
    end_time: null,
    successful: 0,
    failed: 0,
    log_file: null,
    error: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showResults, setShowResults] = useState(false);
  const [logFiles, setLogFiles] = useState<LogFile[]>([]);
  const [selectedLogContent, setSelectedLogContent] = useState<any>(null);
  const [showLogViewer, setShowLogViewer] = useState(false);
  
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    loadInitialData();
    loadLogFiles();
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const tldData = await ApiService.getTlds();
      
      // Separate active and inactive TLDs
      const activeTlds = tldData.filter(tld => tld.is_active);
      const inactiveTlds = tldData.filter(tld => !tld.is_active);
      setTlds(activeTlds);
      setInactiveTlds(inactiveTlds);
      
      // Check current status
      await checkStatus();
      
    } catch (err) {
      setError('Failed to load initial data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLogFiles = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/api/bulk-tld-check/logs`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogFiles(data.logs || []);
      }
    } catch (error) {
      console.log('Could not load log files:', error);
    }
  };

  const checkStatus = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/api/bulk-tld-check/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const newStatus = await response.json();
        setStatus(newStatus);
        
        // Start polling if running
        if (newStatus.running && !intervalRef.current) {
          startStatusPolling();
        } else if (!newStatus.running && intervalRef.current) {
          stopStatusPolling();
          // Reload log files when check completes
          loadLogFiles();
        }
      }
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };

  const startStatusPolling = () => {
    if (intervalRef.current) return;
    
    intervalRef.current = setInterval(async () => {
      await checkStatus();
    }, 1000); // Poll every second
  };

  const stopStatusPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  };

  const startBulkCheck = async (checkInactive: boolean = false) => {
    setError('');
    
    try {
      const response = await fetch(`${config.apiUrl}/api/bulk-tld-check/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ check_inactive: checkInactive })
      });

      if (!response.ok) {
        throw new Error(`Failed to start bulk check: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        startStatusPolling();
      } else {
        setError(result.message || 'Failed to start bulk check');
      }
    } catch (error: any) {
      setError(`Failed to start bulk check: ${error.message}`);
    }
  };

  const stopBulkCheck = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/api/bulk-tld-check/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        stopStatusPolling();
        // Check status immediately to update UI state
        setTimeout(async () => {
          await checkStatus();
          // Reload log files after stopping
          await loadLogFiles();
        }, 500); // Small delay to allow backend to update status
      }
    } catch (error) {
      console.error('Error stopping bulk check:', error);
    }
  };

  const viewLogFile = async (filename: string) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/bulk-tld-check/logs/${filename}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const content = await response.json();
        setSelectedLogContent(content);
        setShowLogViewer(true);
      } else {
        setError('Failed to load log file');
      }
    } catch (error) {
      setError('Error loading log file');
    }
  };

  const downloadLogFile = (filename: string) => {
    const url = `${config.apiUrl}/api/bulk-tld-check/download/${filename}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    
    // Add auth header by creating a form
    const form = document.createElement('form');
    form.method = 'GET';
    form.action = url;
    form.style.display = 'none';
    
    document.body.appendChild(form);
    document.body.appendChild(a);
    
    // For now, just open in new tab since we can't easily add auth headers to download
    window.open(url + `?auth=${localStorage.getItem('auth_token')}`, '_blank');
    
    document.body.removeChild(form);
    document.body.removeChild(a);
  };

  const deleteLogFile = async (filename: string) => {
    if (!window.confirm(`Are you sure you want to delete the log file "${filename}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${config.apiUrl}/api/bulk-tld-check/logs/${filename}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Reload log files list
        await loadLogFiles();
        setError('');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to delete log file');
      }
    } catch (error) {
      setError('Error deleting log file');
      console.error('Error deleting log file:', error);
    }
  };

  const getProgressPercentage = () => {
    if (status.total === 0) return 0;
    return Math.round((status.progress / status.total) * 100);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading TLD data...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Bulk TLD Checker</h1>
        <p>Test active or inactive TLDs and update their status based on lookup results</p>
      </div>

      <div className="content-grid">
        {/* Configuration Panel */}
        <div className="card">
          <div className="card-header">
            <h2>Bulk TLD Check Control</h2>
          </div>
          <div className="card-content">
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-value">{tlds.length}</div>
                <div className="stat-label">Active TLDs</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{inactiveTlds.length}</div>
                <div className="stat-label">Inactive TLDs</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">Python</div>
                <div className="stat-label">Backend Engine</div>
              </div>
            </div>

            <div className="info-box">
              <h4>🤖 Automated Checking</h4>
              <p>This system runs automated TLD checks using a Python backend engine. Failed TLDs are automatically deactivated to improve API performance.</p>
              <p><strong>Active TLD Check:</strong> Tests currently active TLDs and deactivates those that fail.</p>
              <p><strong>Inactive TLD Check:</strong> Tests currently inactive TLDs and reactivates those that work again.</p>
              <p><strong>Cron Job:</strong> Set up monthly automated checks using the provided Python script.</p>
            </div>

            {error && (
              <div className="alert alert-error">
                {error}
              </div>
            )}

            <div className="button-group">
              <button
                onClick={() => startBulkCheck(false)}
                disabled={status.running}
                className="btn btn-primary"
              >
                {status.running ? 'Checking in Progress...' : 'Check Active TLDs'}
              </button>
              
              <button
                onClick={() => startBulkCheck(true)}
                disabled={status.running || inactiveTlds.length === 0}
                className="btn btn-secondary"
              >
                Check Inactive TLDs ({inactiveTlds.length})
              </button>
              
              {status.running && (
                <button
                  onClick={stopBulkCheck}
                  className="btn btn-danger"
                >
                  Stop Check
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Progress Panel */}
        {(status.running || status.results.length > 0) && (
          <div className="card">
            <div className="card-header">
              <h2>Progress</h2>
            </div>
            <div className="card-content">
              <div className="progress-info">
                <div className="progress-text">
                  {status.running ? (
                    <>
                      Checking {status.current_tld} ({status.progress}/{status.total})
                      {status.current_domain && (
                        <div className="current-domain">Testing: {status.current_domain}</div>
                      )}
                    </>
                  ) : (
                    <>Check completed ({status.progress}/{status.total})</>
                  )}
                </div>
                <div className="progress-percentage">{getProgressPercentage()}%</div>
              </div>
              
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${getProgressPercentage()}%` }}
                />
              </div>

              <div className="stats-grid">
                <div className="stat-item success">
                  <div className="stat-value">{status.successful}</div>
                  <div className="stat-label">Successful</div>
                </div>
                <div className="stat-item error">
                  <div className="stat-value">{status.failed}</div>
                  <div className="stat-label">Failed</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{status.progress}</div>
                  <div className="stat-label">Checked</div>
                </div>
              </div>

              {status.start_time && (
                <div className="time-info">
                  <p><strong>Started:</strong> {new Date(status.start_time).toLocaleString()}</p>
                  {status.end_time && (
                    <p><strong>Completed:</strong> {new Date(status.end_time).toLocaleString()}</p>
                  )}
                  {status.error && (
                    <div className="alert alert-error">
                      <strong>Error:</strong> {status.error}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Log Files Panel */}
        {logFiles.length > 0 && (
          <div className="card full-width">
            <div className="card-header">
              <h2>Previous Check Logs</h2>
            </div>
            <div className="card-content">
              <div className="log-files-grid">
                {logFiles.map((logFile, index) => (
                  <div key={index} className="log-file-item">
                    <div className="log-file-info">
                      <div className="log-file-name">{logFile.filename}</div>
                      <div className="log-file-details">
                        <span>Size: {Math.round(logFile.size / 1024)}KB</span>
                        <span>Created: {new Date(logFile.created).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="log-file-actions">
                      <button
                        onClick={() => viewLogFile(logFile.filename)}
                        className="btn btn-secondary btn-sm"
                      >
                        View
                      </button>
                      <button
                        onClick={() => downloadLogFile(logFile.filename)}
                        className="btn btn-primary btn-sm"
                      >
                        Download
                      </button>
                      <button
                        onClick={() => deleteLogFile(logFile.filename)}
                        className="btn btn-danger btn-sm"
                        style={{ marginLeft: '8px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Results Panel */}
        {status.results.length > 0 && (
          <div className="card full-width">
            <div className="card-header">
              <h2>Results</h2>
              <div className="button-group">
                <button
                  onClick={() => setShowResults(!showResults)}
                  className="btn btn-secondary"
                >
                  {showResults ? 'Hide Details' : 'Show Details'}
                </button>
                {status.log_file && (
                  <button
                    onClick={() => downloadLogFile(status.log_file!)}
                    className="btn btn-primary"
                  >
                    Download Current Log
                  </button>
                )}
              </div>
            </div>
            
            {showResults && (
              <div className="card-content">
                <div className="results-table">
                  <div className="table-header">
                    <div>TLD</div>
                    <div>Status</div>
                    <div>Protocol</div>
                    <div>Server</div>
                    <div>Test Domain</div>
                    <div>Errors</div>
                  </div>
                  {status.results.map((result, index) => (
                    <div key={index} className={`table-row ${result.success ? 'success' : 'error'}`}>
                      <div className="tld">{result.tld}</div>
                      <div className="status">
                        {result.success ? '✅ Success' : '❌ Failed'}
                      </div>
                      <div className="protocol">{result.protocol}</div>
                      <div className="server">{result.whois_server || result.rdap_server || 'N/A'}</div>
                      <div className="domain">{result.test_domain}</div>
                      <div className="errors">
                        {result.errors ? result.errors.join(', ') : '-'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Log Viewer Modal */}
        {showLogViewer && selectedLogContent && (
          <div className="modal-overlay" onClick={() => setShowLogViewer(false)}>
            <div className="modal-content log-viewer" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Log Viewer</h2>
                <button
                  onClick={() => setShowLogViewer(false)}
                  className="modal-close"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="log-summary">
                  <div className="log-stats">
                    <span><strong>Total Checked:</strong> {selectedLogContent.total_checked}</span>
                    <span><strong>Successful:</strong> {selectedLogContent.successful}</span>
                    <span><strong>Failed:</strong> {selectedLogContent.failed}</span>
                    <span><strong>Duration:</strong> {selectedLogContent.duration_seconds ? Math.round(selectedLogContent.duration_seconds) + 's' : 'N/A'}</span>
                  </div>
                  <div className="log-timestamp">
                    <strong>Generated:</strong> {new Date(selectedLogContent.timestamp).toLocaleString()}
                  </div>
                </div>
                
                <div className="log-results">
                  <h3>Results ({selectedLogContent.results?.length || 0})</h3>
                  <div className="results-table">
                    <div className="table-header">
                      <div>TLD</div>
                      <div>Status</div>
                      <div>Protocol</div>
                      <div>Server</div>
                      <div>Test Domain</div>
                      <div>Errors</div>
                    </div>
                    {selectedLogContent.results?.map((result: BulkCheckResult, index: number) => (
                      <div key={index} className={`table-row ${result.success ? 'success' : 'error'}`}>
                        <div className="tld">{result.tld}</div>
                        <div className="status">
                          {result.success ? '✅ Success' : '❌ Failed'}
                        </div>
                        <div className="protocol">{result.protocol}</div>
                        <div className="server">{result.whois_server || result.rdap_server || 'N/A'}</div>
                        <div className="domain">{result.test_domain}</div>
                        <div className="errors">
                          {result.errors ? result.errors.join(', ') : '-'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkTldCheck;
